import { useNavigate, Link } from 'react-router-dom';
import { BellRing, Volume2, VolumeX } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import {
  isNotificationSoundEnabled,
  setNotificationSoundEnabled,
} from '@/lib/notificationSound';
import { useState } from 'react';

function formatWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function DemaxtoreNotifications() {
  const navigate = useNavigate();
  const { notifications, unreadCount, markRead, markAllRead, polling } = useNotifications();
  const [soundOn, setSoundOn] = useState(() => isNotificationSoundEnabled());

  const toggleSound = () => {
    const next = !soundOn;
    setSoundOn(next);
    setNotificationSoundEnabled(next);
  };

  return (
    <div className="space-y-5" data-testid="demaxtore-notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Bildirimler</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Yeni RFQ ve CommodityBid talepleri burada görünür
            {polling ? ' · güncelleniyor…' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={toggleSound}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            data-testid="notifications-sound-toggle"
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            {soundOn ? 'Zil sesi açık' : 'Zil sesi kapalı'}
          </button>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Tümünü okundu işaretle
            </button>
          )}
          <Link to="/dashboard" className="text-sm font-medium text-sky-600 hover:text-sky-700 px-2 py-2">
            Dashboard
          </Link>
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-14 h-14 bg-sky-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <BellRing className="w-7 h-7 text-sky-600" />
          </div>
          <p className="text-slate-700 font-medium">Henüz bildirim yok</p>
          <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">
            Yeni bir RFQ gönderdiğinizde veya CommodityBid üzerinden teklif oluşturduğunuzda kısa bir zil
            sesi duyarsınız ve kayıt burada listelenir.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {notifications.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => {
                  markRead(n.id);
                  navigate(n.href);
                }}
                className={`w-full text-left rounded-xl border px-4 py-3.5 transition-colors ${
                  n.read
                    ? 'border-slate-100 bg-white hover:bg-slate-50'
                    : 'border-sky-100 bg-sky-50/40 hover:bg-sky-50/70'
                }`}
                data-testid={`notification-item-${n.type}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                      {n.platformLabel}
                    </p>
                    <p className="text-sm font-semibold text-slate-900 mt-0.5 truncate">{n.title}</p>
                    <p className="text-xs text-slate-600 mt-1">{n.message}</p>
                  </div>
                  <span className="shrink-0 text-xs text-slate-400">{formatWhen(n.createdAt)}</span>
                </div>
                {!n.read && (
                  <span className="mt-2 inline-block text-[11px] font-medium text-sky-700">Yeni</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => navigate('/demaxtore/rfqs')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
        >
          RFQ&apos;larım
        </button>
        <button
          type="button"
          onClick={() => navigate('/commoditybid/live-bids')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
        >
          Bekleyen teklifler
        </button>
      </div>
    </div>
  );
}
